/**
 * The primary account a card is going under, and the one question that follows
 * from it.
 *
 * A supplementary takes its tier from the primary above it. The three tiers the
 * club signs up itself — complimentary, employee, affiliates — are recorded as
 * Benefits, and a member is never acquired that way; so an account carrying
 * Benefits that lands under a *Member* primary has to say which channel it came
 * in through, exactly as a change of tier does. Nothing else about the move
 * asks anything, which is why the picker appears only for that one pairing.
 *
 * The typed number is looked up so the form can know the answer before the save
 * does. A number nobody holds asks nothing: the server will refuse the move on
 * its own, and a picker under an unknown account would be a question about
 * nothing.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useMemberChannels } from "@/components/member-acquisition";
import { Field } from "@/components/ui/field";
import { OptionSelect } from "@/components/ui/option-select";
import { api } from "@/services/api";
import { useTranslation } from "@/store/locale";

/** What the lookup answers with; only the tier is read here. */
interface LookupHit {
  account_number: string;
  membership_tier: string;
}

/** Long enough that a number is not looked up on every keystroke of typing it. */
const LOOKUP_DEBOUNCE_MS = 400;

/**
 * The primary this number names, once the typing settles.
 *
 * `null` while nothing has been typed, while the answer is still coming, and
 * for a number nobody holds — three states the caller treats alike, because
 * none of them is a Member-tier primary to ask a channel for.
 */
export function usePrimaryAccount(accountNumber: string) {
  const number = accountNumber.trim();
  const [settled, setSettled] = useState(number);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(number), LOOKUP_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [number]);

  const hit = useQuery({
    queryKey: ["/members/lookup", settled],
    queryFn: async ({ signal }) =>
      (await api.get<LookupHit>("/members/lookup", { params: { q: settled }, signal })).data,
    enabled: settled.length > 0,
    // A number nobody holds is an answer, not a failure worth retrying.
    retry: false,
    staleTime: 60_000,
  });

  return hit.data ?? null;
}

/**
 * Whether moving this account under that primary leaves a question open.
 *
 * Both halves matter: an account already on a channel a member can have keeps
 * it, and a primary on one of the benefit tiers settles the channel itself.
 */
export const needsAChannel = (
  currentAcquisition: string | null | undefined,
  primary: { membership_tier: string } | null,
) => currentAcquisition === "benefits" && primary?.membership_tier === "member";

export function AcquisitionForTheMove({
  value,
  onChange,
  id = "move-acquisition",
  hint,
  current,
}: {
  value: string;
  onChange: (next: string) => void;
  id?: string;
  /** Why it is being asked, when the default sentence is not the reason. */
  hint?: string | undefined;
  /** The channel the account holds now: on the list whatever the rank. */
  current?: string | null;
}) {
  const t = useTranslation();
  // The move is onto a member's card, so the channels are a member's — and
  // which of them this person may set is the same question the sheet beside
  // it asks. `value` is what has been chosen here, not a channel on record,
  // so there is nothing of the member's own to keep on the list.
  const choices = useMemberChannels(current);
  return (
    <Field
      label={t("mem.acquisitionType")}
      htmlFor={id}
      required
      hint={hint ?? t("md.channelForTheMove")}
    >
      <OptionSelect
        id={id}
        value={value}
        onChange={onChange}
        required
        placeholder={t("new.selectAcquisition")}
        choices={choices}
      />
    </Field>
  );
}
